import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Palette,
  Type,
  Image,
  Globe,
  Mail,
  Layers,
  Save,
  Eye,
  RotateCcw,
  Upload,
  Check,
  X,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ExternalLink,
  History,
  Shield,
  Clock,
  Copy,
  CheckCircle,
  Info,
  Settings,
  Layout,
  Sun,
  Moon
} from 'lucide-react'
import { Select } from '../components/ui/Select'
import { PageHeader } from '../components/ui/PageHeader'
import {
  whitelabelApi,
  type PartnerBrandingConfig,
  type BrandingTemplate,
  type BrandingColors,
  type BrandingTypography,
  type BrandingTheme,
  type BrandingContent,
  type BrandingContact,
  type BrandingSocial,
  type BrandingEmail,
  type BrandingSeo,
  type BrandingCustomCss,
  type BrandingHistory,
  type DomainVerification,
  type WhitelabelAssetType
} from '../services/api'

type TabType = 'colors' | 'typography' | 'logo' | 'content' | 'domain' | 'email' | 'templates'
type LogoAssetKey = 'primary' | 'secondary' | 'favicon' | 'emailHeader'

export function Whitelabel() {
  const [activeTab, setActiveTab] = useState<TabType>('colors')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [branding, setBranding] = useState<PartnerBrandingConfig | null>(null)
  const [templates, setTemplates] = useState<BrandingTemplate[]>([])
  const [history, setHistory] = useState<BrandingHistory[]>([])
  const [hasChanges, setHasChanges] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [uploadingAssets, setUploadingAssets] = useState<Partial<Record<LogoAssetKey, boolean>>>({})

  // Form states
  const [colors, setColors] = useState<BrandingColors | null>(null)
  const [typography, setTypography] = useState<BrandingTypography | null>(null)
  const [theme, setTheme] = useState<BrandingTheme | null>(null)
  const [content, setContent] = useState<BrandingContent | null>(null)
  const [contact, setContact] = useState<BrandingContact | null>(null)
  const [social, setSocial] = useState<BrandingSocial | null>(null) // kept for save/load compatibility
  const [email, setEmail] = useState<BrandingEmail | null>(null)
  const [seo, setSeo] = useState<BrandingSeo | null>(null)
  const [customCss, setCustomCss] = useState<BrandingCustomCss | null>(null)
  const [customDomain, setCustomDomain] = useState('')
  const [domainVerification, setDomainVerification] = useState<DomainVerification | null>(null)
  const [verifyingDomain, setVerifyingDomain] = useState(false)
  const [requestingSsl, setRequestingSsl] = useState(false)
  const [generatingPreview, setGeneratingPreview] = useState(false)
  const [uploadingOgImage, setUploadingOgImage] = useState(false) // kept for OG image upload compatibility

  const ogImageInputRef = useRef<HTMLInputElement | null>(null)

  const logoInputRefs = {
    primary: useRef<HTMLInputElement | null>(null),
    secondary: useRef<HTMLInputElement | null>(null),
    favicon: useRef<HTMLInputElement | null>(null),
    emailHeader: useRef<HTMLInputElement | null>(null),
  }

  const assetRules: Record<LogoAssetKey, { assetType: WhitelabelAssetType; maxSize: number; accept: string; allowedTypes: string[] }> = {
    primary: {
      assetType: 'logo',
      maxSize: 2 * 1024 * 1024,
      accept: 'image/png,image/jpeg,image/svg+xml',
      allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'],
    },
    secondary: {
      assetType: 'secondary-logo',
      maxSize: 500 * 1024,
      accept: 'image/png,image/svg+xml',
      allowedTypes: ['image/png', 'image/svg+xml'],
    },
    favicon: {
      assetType: 'favicon',
      maxSize: 500 * 1024,
      accept: 'image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon',
      allowedTypes: ['image/png', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'],
    },
    emailHeader: {
      assetType: 'email-header',
      maxSize: 1024 * 1024,
      accept: 'image/png,image/jpeg',
      allowedTypes: ['image/png', 'image/jpeg', 'image/jpg'],
    },
  }

  // Fetch branding config
  const fetchBranding = useCallback(async () => {
    try {
      setLoading(true)
      const data = await whitelabelApi.getBranding()
      setBranding(data)
      setColors(data.colors)
      setTypography(data.typography)
      setTheme(data.theme)
      setContent(data.content)
      setContact(data.contact)
      setSocial(data.social)
      setEmail(data.email)
      setSeo(data.seo)
      setCustomCss(data.customCss)
      setCustomDomain(data.domain.customDomain || '')
      setHasChanges(false)
    } catch (err) {
      console.error('Error fetching branding:', err)
      setError('Erro ao carregar configuracoes de marca')
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const data = await whitelabelApi.getTemplates()
      setTemplates(data)
    } catch (err) {
      console.error('Error fetching templates:', err)
    }
  }, [])

  // Fetch history
  const fetchHistory = useCallback(async () => {
    try {
      const data = await whitelabelApi.getHistory()
      setHistory(data)
    } catch (err) {
      console.error('Error fetching history:', err)
    }
  }, [])

  useEffect(() => {
    fetchBranding()
    fetchTemplates()
    fetchHistory()
  }, [fetchBranding, fetchTemplates, fetchHistory])

  useEffect(() => {
    setDomainVerification(null)
  }, [customDomain])

  // Save changes
  const handleSave = async () => {
    if (!branding) return

    try {
      setSaving(true)
      setError(null)

      const updates: Partial<PartnerBrandingConfig> = {}

      if (colors && JSON.stringify(colors) !== JSON.stringify(branding.colors)) {
        updates.colors = colors
      }
      if (typography && JSON.stringify(typography) !== JSON.stringify(branding.typography)) {
        updates.typography = typography
      }
      if (theme && JSON.stringify(theme) !== JSON.stringify(branding.theme)) {
        updates.theme = theme
      }
      if (content && JSON.stringify(content) !== JSON.stringify(branding.content)) {
        updates.content = content
      }
      if (contact && JSON.stringify(contact) !== JSON.stringify(branding.contact)) {
        updates.contact = contact
      }
      if (social && JSON.stringify(social) !== JSON.stringify(branding.social)) {
        updates.social = social
      }
      if (email && JSON.stringify(email) !== JSON.stringify(branding.email)) {
        updates.email = email
      }
      if (seo && JSON.stringify(seo) !== JSON.stringify(branding.seo)) {
        updates.seo = seo
      }
      if (customCss && JSON.stringify(customCss) !== JSON.stringify(branding.customCss)) {
        updates.customCss = customCss
      }

      if (Object.keys(updates).length > 0) {
        const updated = await whitelabelApi.updateBranding(updates)
        setBranding(updated)
        setHasChanges(false)
        setSuccess('Alteracoes salvas com sucesso!')
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      console.error('Error saving branding:', err)
      setError('Erro ao salvar alteracoes')
    } finally {
      setSaving(false)
    }
  }

  // Publish changes
  const handlePublish = async () => {
    try {
      setSaving(true)
      await handleSave()
      const result = await whitelabelApi.publishBranding()
      if (result.success) {
        setSuccess(`Marca publicada com sucesso! Versao ${result.version}`)
        fetchBranding()
        fetchHistory()
      }
    } catch (err) {
      console.error('Error publishing branding:', err)
      setError('Erro ao publicar marca')
    } finally {
      setSaving(false)
    }
  }

  const handleVerifyDomain = async () => {
    const domain = customDomain.trim().toLowerCase()
    if (!domain) {
      setError('Informe um dominio valido para verificar')
      return
    }

    try {
      setVerifyingDomain(true)
      setError(null)
      const result = await whitelabelApi.verifyDomain(domain)
      setDomainVerification(result)
      await fetchBranding()
      if (result.verified) {
        setSuccess('Dominio verificado com sucesso!')
      } else if (result.error) {
        setError(result.error)
      }
    } catch (err) {
      console.error('Error verifying domain:', err)
      setError('Erro ao verificar dominio')
    } finally {
      setVerifyingDomain(false)
    }
  }

  const handleRequestSsl = async () => {
    const domain = branding?.domain.customDomain || customDomain.trim().toLowerCase()
    if (!domain) {
      setError('Defina um dominio para solicitar SSL')
      return
    }

    try {
      setRequestingSsl(true)
      setError(null)
      const result = await whitelabelApi.requestSSL(domain)
      setSuccess(result.message)
    } catch (err) {
      console.error('Error requesting SSL:', err)
      setError('Erro ao solicitar SSL')
    } finally {
      setRequestingSsl(false)
    }
  }

  // Reset to default
  const handleReset = async () => {
    if (!confirm('Tem certeza que deseja resetar todas as configuracoes para o padrao?')) return

    try {
      setSaving(true)
      const data = await whitelabelApi.resetToDefault()
      setBranding(data)
      setColors(data.colors)
      setTypography(data.typography)
      setTheme(data.theme)
      setContent(data.content)
      setContact(data.contact)
      setSocial(data.social)
      setEmail(data.email)
      setSeo(data.seo)
      setCustomCss(data.customCss)
      setHasChanges(false)
      setSuccess('Configuracoes resetadas para o padrao')
    } catch (err) {
      console.error('Error resetting branding:', err)
      setError('Erro ao resetar configuracoes')
    } finally {
      setSaving(false)
    }
  }

  // Apply template
  const handleApplyTemplate = async (templateId: string) => {
    try {
      setSaving(true)
      const data = await whitelabelApi.applyTemplate(templateId)
      setBranding(data)
      setColors(data.colors)
      setTypography(data.typography)
      setTheme(data.theme)
      setHasChanges(false)
      setSuccess('Template aplicado com sucesso!')
    } catch (err) {
      console.error('Error applying template:', err)
      setError('Erro ao aplicar template')
    } finally {
      setSaving(false)
    }
  }

  // Mark as changed
  const markChanged = () => {
    setHasChanges(true)
  }

  // Generate preview URL and open in new tab
  const handleGeneratePreview = async () => {
    try {
      setGeneratingPreview(true)
      setError(null)

      // Save changes first if there are any
      if (hasChanges) {
        await handleSave()
      }

      const result = await whitelabelApi.generatePreview()
      window.open(result.previewUrl, '_blank')
      setSuccess(`Preview gerado! Valido ate ${new Date(result.expiresAt).toLocaleString('pt-BR')}`)
    } catch (err) {
      console.error('Error generating preview:', err)
      setError('Erro ao gerar preview')
    } finally {
      setGeneratingPreview(false)
    }
  }

  // Handle OG Image upload
  const handleOgImageUpload = async (file?: File) => {
    if (!file || !branding || !seo) return

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg']
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de arquivo invalido. Use PNG ou JPG.')
      return
    }

    const maxSize = 2 * 1024 * 1024 // 2MB
    if (file.size > maxSize) {
      setError('Arquivo excede o tamanho maximo de 2MB.')
      return
    }

    try {
      setError(null)
      setUploadingOgImage(true)

      const uploadResult = await whitelabelApi.uploadAsset('og-image', file)
      setSeo({ ...seo, ogImage: uploadResult.url })
      markChanged()
      setSuccess('Imagem OG enviada com sucesso!')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error uploading OG image:', err)
      setError('Erro ao fazer upload da imagem OG')
    } finally {
      setUploadingOgImage(false)
    }
  }

  const handleOgImageInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    await handleOgImageUpload(file)
  }

  const setAssetUploading = (assetKey: LogoAssetKey, value: boolean) => {
    setUploadingAssets(prev => ({ ...prev, [assetKey]: value }))
  }

  const handleAssetUpload = async (assetKey: LogoAssetKey, file?: File) => {
    if (!file || !branding) return

    const rules = assetRules[assetKey]
    if (!rules.allowedTypes.includes(file.type)) {
      setError('Tipo de arquivo inválido para este asset.')
      return
    }

    if (file.size > rules.maxSize) {
      setError('Arquivo excede o tamanho máximo permitido.')
      return
    }

    try {
      setError(null)
      setAssetUploading(assetKey, true)

      const uploadResult = await whitelabelApi.uploadAsset(rules.assetType, file)
      const updatedLogo = {
        ...branding.logo,
        [assetKey]: uploadResult.url,
      }
      const updated = await whitelabelApi.updateLogo(updatedLogo)
      setBranding(updated)
      setHasChanges(false)
      setSuccess('Arquivo enviado com sucesso!')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error uploading asset:', err)
      setError('Erro ao fazer upload do arquivo')
    } finally {
      setAssetUploading(assetKey, false)
    }
  }

  const handleFileInputChange = async (
    assetKey: LogoAssetKey,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    await handleAssetUpload(assetKey, file)
  }

  // Tabs config
  const tabs: { id: TabType; label: string; icon: typeof Palette }[] = [
    { id: 'colors', label: 'Cores', icon: Palette },
    { id: 'typography', label: 'Tipografia', icon: Type },
    { id: 'logo', label: 'Logo', icon: Image },
    { id: 'content', label: 'Conteudo', icon: Layout },
    { id: 'domain', label: 'Dominio', icon: Globe },
    { id: 'email', label: 'Email', icon: Mail },




    { id: 'templates', label: 'Templates', icon: Layers }
  ]

  // Render color picker
  const renderColorInput = (label: string, value: string, onChange: (v: string) => void) => (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={value}
            onChange={(e) => { onChange(e.target.value); markChanged() }}
            className="w-10 h-10 rounded border cursor-pointer"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => { onChange(e.target.value); markChanged() }}
            className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
            placeholder="#000000"
          />
        </div>
      </div>
    </div>
  )

  // Render Colors Tab
  const renderColorsTab = () => {
    if (!colors) return null

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Primary Colors */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.primary }} />
              Cores Principais
            </h4>
            <div className="space-y-4">
              {renderColorInput('Primaria', colors.primary, (v) => setColors({ ...colors, primary: v }))}
              {renderColorInput('Secundaria', colors.secondary, (v) => setColors({ ...colors, secondary: v }))}
              {renderColorInput('Destaque', colors.accent, (v) => setColors({ ...colors, accent: v }))}
            </div>
          </div>

          {/* Background Colors */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full border" style={{ backgroundColor: colors.background }} />
              Fundos
            </h4>
            <div className="space-y-4">
              {renderColorInput('Fundo', colors.background, (v) => setColors({ ...colors, background: v }))}
              {renderColorInput('Superficie', colors.surface, (v) => setColors({ ...colors, surface: v }))}
            </div>
          </div>

          {/* Text Colors */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <Type size={14} />
              Textos
            </h4>
            <div className="space-y-4">
              {renderColorInput('Texto Principal', colors.textPrimary, (v) => setColors({ ...colors, textPrimary: v }))}
              {renderColorInput('Texto Secundario', colors.textSecondary, (v) => setColors({ ...colors, textSecondary: v }))}
            </div>
          </div>

          {/* Status Colors */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4">Status</h4>
            <div className="space-y-4">
              {renderColorInput('Sucesso', colors.success, (v) => setColors({ ...colors, success: v }))}
              {renderColorInput('Alerta', colors.warning, (v) => setColors({ ...colors, warning: v }))}
              {renderColorInput('Erro', colors.error, (v) => setColors({ ...colors, error: v }))}
              {renderColorInput('Info', colors.info, (v) => setColors({ ...colors, info: v }))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-4">Pre-visualizacao</h4>
          <div className="p-6 rounded-lg" style={{ backgroundColor: colors.background }}>
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: colors.surface }}>
              <h3 className="text-lg font-bold mb-2" style={{ color: colors.textPrimary }}>Titulo de Exemplo</h3>
              <p style={{ color: colors.textSecondary }}>Este e um texto de exemplo para visualizar as cores.</p>
            </div>
            <div className="flex gap-2">
              <button className="px-4 py-2 rounded-lg text-white" style={{ backgroundColor: colors.primary }}>
                Botao Primario
              </button>
              <button className="px-4 py-2 rounded-lg text-white" style={{ backgroundColor: colors.secondary }}>
                Secundario
              </button>
              <button className="px-4 py-2 rounded-lg text-white" style={{ backgroundColor: colors.accent }}>
                Destaque
              </button>
            </div>
            <div className="flex gap-2 mt-4">
              <span className="px-3 py-1 rounded-full text-white text-sm" style={{ backgroundColor: colors.success }}>Sucesso</span>
              <span className="px-3 py-1 rounded-full text-white text-sm" style={{ backgroundColor: colors.warning }}>Alerta</span>
              <span className="px-3 py-1 rounded-full text-white text-sm" style={{ backgroundColor: colors.error }}>Erro</span>
              <span className="px-3 py-1 rounded-full text-white text-sm" style={{ backgroundColor: colors.info }}>Info</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Render Typography Tab
  const renderTypographyTab = () => {
    if (!typography) return null

    const fontOptions = [
      'Inter, sans-serif',
      'Roboto, sans-serif',
      'Open Sans, sans-serif',
      'Lato, sans-serif',
      'Poppins, sans-serif',
      'Source Sans Pro, sans-serif',
      'Plus Jakarta Sans, sans-serif',
      'DM Sans, sans-serif',
      'IBM Plex Sans, sans-serif',
      'Georgia, serif',
      'Merriweather, serif',
      'Playfair Display, serif',
    ]

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Font Family */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4">Fontes</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fonte Principal</label>
                <Select
                  value={typography.fontFamily}
                  onChange={(e) => { setTypography({ ...typography, fontFamily: e.target.value }); markChanged() }}
                  options={fontOptions.map(font => ({
                    value: font,
                    label: font.split(',')[0]
                  }))}
                  fullWidth
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fonte Secundaria</label>
                <Select
                  value={typography.fontFamilySecondary}
                  onChange={(e) => { setTypography({ ...typography, fontFamilySecondary: e.target.value }); markChanged() }}
                  options={fontOptions.map(font => ({
                    value: font,
                    label: font.split(',')[0]
                  }))}
                  fullWidth
                />
              </div>
            </div>
          </div>

          {/* Size and Weight */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4">Tamanho e Peso</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tamanho Base</label>
                <Select
                  value={typography.fontSize}
                  onChange={(e) => { setTypography({ ...typography, fontSize: e.target.value }); markChanged() }}
                  options={[
                    { value: '14px', label: '14px - Compacto' },
                    { value: '15px', label: '15px - Pequeno' },
                    { value: '16px', label: '16px - Normal' },
                    { value: '17px', label: '17px - Grande' },
                    { value: '18px', label: '18px - Muito Grande' }
                  ]}
                  fullWidth
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Peso Normal: {typography.fontWeightNormal}</label>
                <input
                  type="range"
                  min="300"
                  max="500"
                  step="100"
                  value={typography.fontWeightNormal}
                  onChange={(e) => { setTypography({ ...typography, fontWeightNormal: parseInt(e.target.value) }); markChanged() }}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Peso Negrito: {typography.fontWeightBold}</label>
                <input
                  type="range"
                  min="500"
                  max="800"
                  step="100"
                  value={typography.fontWeightBold}
                  onChange={(e) => { setTypography({ ...typography, fontWeightBold: parseInt(e.target.value) }); markChanged() }}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Altura de Linha: {typography.lineHeight}</label>
                <input
                  type="range"
                  min="1.2"
                  max="2"
                  step="0.1"
                  value={typography.lineHeight}
                  onChange={(e) => { setTypography({ ...typography, lineHeight: parseFloat(e.target.value) }); markChanged() }}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-4">Pre-visualizacao</h4>
          <div
            className="p-6 bg-gray-50 rounded-lg"
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.fontSize,
              fontWeight: typography.fontWeightNormal,
              lineHeight: typography.lineHeight
            }}
          >
            <h1 style={{ fontWeight: typography.fontWeightBold, fontSize: '2em', fontFamily: typography.fontFamilySecondary }} className="mb-2">
              Titulo Principal
            </h1>
            <h2 style={{ fontWeight: typography.fontWeightBold, fontSize: '1.5em' }} className="mb-2">
              Subtitulo da Pagina
            </h2>
            <p className="mb-4">
              Este e um paragrafo de exemplo que demonstra como o texto vai aparecer na sua aplicacao.
              A tipografia e fundamental para a experiencia do usuario.
            </p>
            <p className="text-sm text-gray-500">
              Texto pequeno para informacoes secundarias ou legendas.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Render Logo Tab
  const renderLogoTab = () => {
    if (!branding) return null

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Primary Logo */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4">Logo Principal</h4>
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              {branding.logo.primary ? (
                <img src={branding.logo.primary} alt="Logo" className="max-h-20 mx-auto mb-4" />
              ) : (
                <Image className="mx-auto text-gray-300 mb-4" size={48} />
              )}
              <input
                ref={logoInputRefs.primary}
                type="file"
                accept={assetRules.primary.accept}
                onChange={(event) => handleFileInputChange('primary', event)}
                className="hidden"
              />
              <button
                onClick={() => logoInputRefs.primary.current?.click()}
                disabled={uploadingAssets.primary}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto disabled:opacity-50"
              >
                {uploadingAssets.primary ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploadingAssets.primary ? 'Enviando...' : 'Fazer Upload'}
              </button>
              <p className="text-xs text-gray-500 mt-2">PNG, JPG ou SVG. Max 2MB. Recomendado: 400x100px</p>
            </div>
          </div>

          {/* Secondary Logo */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4">Logo Secundario (Icone)</h4>
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              {branding.logo.secondary ? (
                <img src={branding.logo.secondary} alt="Logo Secundario" className="max-h-16 mx-auto mb-4" />
              ) : (
                <Image className="mx-auto text-gray-300 mb-4" size={48} />
              )}
              <input
                ref={logoInputRefs.secondary}
                type="file"
                accept={assetRules.secondary.accept}
                onChange={(event) => handleFileInputChange('secondary', event)}
                className="hidden"
              />
              <button
                onClick={() => logoInputRefs.secondary.current?.click()}
                disabled={uploadingAssets.secondary}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto disabled:opacity-50"
              >
                {uploadingAssets.secondary ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploadingAssets.secondary ? 'Enviando...' : 'Fazer Upload'}
              </button>
              <p className="text-xs text-gray-500 mt-2">PNG ou SVG. Max 500KB. Recomendado: 64x64px</p>
            </div>
          </div>

          {/* Favicon */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4">Favicon</h4>
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              {branding.logo.favicon ? (
                <img src={branding.logo.favicon} alt="Favicon" className="w-8 h-8 mx-auto mb-4" />
              ) : (
                <div className="w-8 h-8 mx-auto mb-4 bg-gray-200 rounded" />
              )}
              <input
                ref={logoInputRefs.favicon}
                type="file"
                accept={assetRules.favicon.accept}
                onChange={(event) => handleFileInputChange('favicon', event)}
                className="hidden"
              />
              <button
                onClick={() => logoInputRefs.favicon.current?.click()}
                disabled={uploadingAssets.favicon}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto disabled:opacity-50"
              >
                {uploadingAssets.favicon ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploadingAssets.favicon ? 'Enviando...' : 'Fazer Upload'}
              </button>
              <p className="text-xs text-gray-500 mt-2">ICO, PNG ou SVG. 32x32px ou 16x16px</p>
            </div>
          </div>

          {/* Email Header */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4">Logo para Emails</h4>
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              {branding.logo.emailHeader ? (
                <img src={branding.logo.emailHeader} alt="Email Logo" className="max-h-12 mx-auto mb-4" />
              ) : (
                <Mail className="mx-auto text-gray-300 mb-4" size={48} />
              )}
              <input
                ref={logoInputRefs.emailHeader}
                type="file"
                accept={assetRules.emailHeader.accept}
                onChange={(event) => handleFileInputChange('emailHeader', event)}
                className="hidden"
              />
              <button
                onClick={() => logoInputRefs.emailHeader.current?.click()}
                disabled={uploadingAssets.emailHeader}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto disabled:opacity-50"
              >
                {uploadingAssets.emailHeader ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploadingAssets.emailHeader ? 'Enviando...' : 'Fazer Upload'}
              </button>
              <p className="text-xs text-gray-500 mt-2">PNG ou JPG. Max 1MB. Recomendado: 300x60px</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Render Content Tab
  const renderContentTab = () => {
    if (!content) return null

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-4">Informacoes da Marca</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Marca</label>
              <input
                type="text"
                value={content.brandName}
                onChange={(e) => { setContent({ ...content, brandName: e.target.value }); markChanged() }}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Nome da sua empresa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slogan</label>
              <input
                type="text"
                value={content.tagline}
                onChange={(e) => { setContent({ ...content, tagline: e.target.value }); markChanged() }}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Seu slogan"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de Login</label>
              <textarea
                value={content.loginMessage}
                onChange={(e) => { setContent({ ...content, loginMessage: e.target.value }); markChanged() }}
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Mensagem exibida na tela de login"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Texto do Rodape</label>
              <input
                type="text"
                value={content.footerText}
                onChange={(e) => { setContent({ ...content, footerText: e.target.value }); markChanged() }}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Texto do rodapé"
              />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={content.showPoweredBy}
                  onChange={(e) => { setContent({ ...content, showPoweredBy: e.target.checked }); markChanged() }}
                  className="rounded"
                />
                <span className="text-sm">Exibir "Powered by NGC Medical"</span>
              </label>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        {contact && (
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium mb-4">Informacoes de Contato</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) => { setContact({ ...contact, email: e.target.value }); markChanged() }}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="contato@empresa.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input
                  type="text"
                  value={contact.phone}
                  onChange={(e) => { setContact({ ...contact, phone: e.target.value }); markChanged() }}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="(11) 99999-9999"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                <input
                  type="text"
                  value={contact.whatsapp}
                  onChange={(e) => { setContact({ ...contact, whatsapp: e.target.value }); markChanged() }}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="5511999999999"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereco</label>
                <input
                  type="text"
                  value={contact.address}
                  onChange={(e) => { setContact({ ...contact, address: e.target.value }); markChanged() }}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Rua, numero, cidade"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render Domain Tab
  const renderDomainTab = () => {
    if (!branding) return null

    return (
      <div className="space-y-6">
        {/* Current Domain */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-4">Dominio Atual</h4>
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <Globe className="text-blue-600" size={24} />
            <div className="flex-1">
              <p className="font-medium">{branding.domain.subdomain}.ngcmed.com.br</p>
              <p className="text-sm text-gray-500">Subdominio padrao</p>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center gap-1">
              <CheckCircle size={14} />
              Ativo
            </span>
          </div>
        </div>

        {/* Custom Domain */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-4">Dominio Personalizado</h4>
          <p className="text-sm text-gray-500 mb-4">
            Configure um dominio proprio para sua aplicacao (ex: app.suaempresa.com.br)
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dominio</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-lg"
                  placeholder="app.suaempresa.com.br"
                />
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                  onClick={handleVerifyDomain}
                  disabled={verifyingDomain}
                >
                  {verifyingDomain ? 'Verificando...' : 'Verificar'}
                </button>
              </div>
            </div>

            {domainVerification?.instructions?.length ? (
              <div className="p-4 bg-blue-50 rounded-lg">
                <h5 className="font-medium text-blue-800 mb-2">Instrucoes de Configuracao</h5>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  {domainVerification.instructions.map((instruction, index) => (
                    <li key={`domain-instruction-${index}`}>{instruction}</li>
                  ))}
                </ol>
              </div>
            ) : branding.domain.customDomain ? (
              <div className="p-4 bg-blue-50 rounded-lg">
                <h5 className="font-medium text-blue-800 mb-2">Instrucoes de Configuracao</h5>
                <p className="text-sm text-blue-700">
                  Clique em "Verificar" para gerar o token DNS TXT.
                </p>
              </div>
            ) : null}

            {/* SSL Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Shield className={branding.domain.sslEnabled ? 'text-green-600' : 'text-gray-400'} size={20} />
                <div>
                  <p className="font-medium">Certificado SSL</p>
                  <p className="text-sm text-gray-500">
                    {branding.domain.sslEnabled ? 'Ativo e valido' : 'Nao configurado'}
                  </p>
                </div>
              </div>
              {!branding.domain.sslEnabled && (
                <button
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-60"
                  onClick={handleRequestSsl}
                  disabled={requestingSsl}
                >
                  {requestingSsl ? 'Solicitando...' : 'Solicitar SSL'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Render Email Tab
  const renderEmailTab = () => {
    if (!email) return null

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-4">Configuracoes de Email</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Remetente</label>
              <input
                type="text"
                value={email.fromName}
                onChange={(e) => { setEmail({ ...email, fromName: e.target.value }); markChanged() }}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Sua Empresa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email do Remetente</label>
              <input
                type="email"
                value={email.fromAddress}
                onChange={(e) => { setEmail({ ...email, fromAddress: e.target.value }); markChanged() }}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="noreply@empresa.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responder Para</label>
              <input
                type="email"
                value={email.replyTo}
                onChange={(e) => { setEmail({ ...email, replyTo: e.target.value }); markChanged() }}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="contato@empresa.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cor do Cabecalho</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={email.headerColor}
                  onChange={(e) => { setEmail({ ...email, headerColor: e.target.value }); markChanged() }}
                  className="w-12 h-10 rounded border cursor-pointer"
                />
                <input
                  type="text"
                  value={email.headerColor}
                  onChange={(e) => { setEmail({ ...email, headerColor: e.target.value }); markChanged() }}
                  className="flex-1 px-3 py-2 border rounded-lg font-mono"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Assinatura</label>
              <textarea
                value={email.signature}
                onChange={(e) => { setEmail({ ...email, signature: e.target.value }); markChanged() }}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                placeholder="Atenciosamente,&#10;Equipe Sua Empresa"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Texto do Rodape</label>
              <input
                type="text"
                value={email.footerText}
                onChange={(e) => { setEmail({ ...email, footerText: e.target.value }); markChanged() }}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Voce recebeu este email porque..."
              />
            </div>
          </div>
        </div>
      </div>
    )
  }





  // Render Theme Tab


  // Render Custom CSS Tab


  // Render Templates Tab
  const renderTemplatesTab = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium mb-4">Templates Pre-definidos</h4>
          <p className="text-sm text-gray-500 mb-4">
            Escolha um template para aplicar rapidamente um conjunto de cores, tipografia e configuracoes de tema.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div
                  className="h-24 flex items-center justify-center"
                  style={{ backgroundColor: template.colors.primary }}
                >
                  <span className="text-white font-bold text-lg">{template.name}</span>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex items-center gap-1 mb-3">
                    {Object.values(template.colors).slice(0, 5).map((color, i) => (
                      <div
                        key={i}
                        className="w-6 h-6 rounded-full border"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 capitalize">{template.category}</span>
                    <button
                      onClick={() => handleApplyTemplate(template.id)}
                      disabled={saving}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>


      </div>
    )
  }

  // Main render
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-gray-400" size={40} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Whitelabel"
        subtitle="Personalize a aparencia da sua plataforma"
        actions={
          <>
            {hasChanges && (
              <span className="text-sm text-orange-600 flex items-center gap-1">
                <AlertTriangle size={14} />
                Alteracoes nao salvas
              </span>
            )}
            <button
              onClick={handleGeneratePreview}
              disabled={saving || generatingPreview}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            >
              {generatingPreview ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
              Preview
            </button>
            <button
              onClick={handleReset}
              disabled={saving}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            >
              <RotateCcw size={16} />
              Resetar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Salvar
            </button>
            <button
              onClick={handlePublish}
              disabled={saving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
            >
              <Check size={16} />
              Publicar
            </button>
          </>
        }
      />

      {/* Status bar */}
      {branding && (
        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg text-sm">
          <span className={`px-2 py-1 rounded ${
            branding.status === 'published' ? 'bg-green-100 text-green-700' :
            branding.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {branding.status === 'published' ? 'Publicado' : branding.status === 'draft' ? 'Rascunho' : 'Em Revisao'}
          </span>
          <span className="text-gray-500">
            Versao: {branding.publishedVersion}
          </span>
          {branding.lastPublished && (
            <span className="text-gray-500 flex items-center gap-1">
              <Clock size={14} />
              Ultima publicacao: {new Date(branding.lastPublished).toLocaleString('pt-BR')}
            </span>
          )}
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle size={20} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <CheckCircle size={20} />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const TabIcon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <TabIcon size={16} />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'colors' && renderColorsTab()}
        {activeTab === 'typography' && renderTypographyTab()}
        {activeTab === 'logo' && renderLogoTab()}
        {activeTab === 'content' && renderContentTab()}
        {activeTab === 'domain' && renderDomainTab()}
        {activeTab === 'email' && renderEmailTab()}




        {activeTab === 'templates' && renderTemplatesTab()}
      </div>
    </div>
  )
}
